import { useMemo, useState } from "react";
import CommonButton from "../common/CommonButton";
import translations from "../common/Translations";
import ModalCloseButton from "../common/ModalCloseButton";
import {
  AssignScopeTabs,
  GroupSelectList,
  filterGroupsByAssignedMembers,
  getCurrentUserGroupIds,
  getGroupMemberIdSet,
  groupMatchesQuery,
  normalizeGroups,
} from "./GroupAssignParts";

const AVATAR_COLORS = ["#2563eb", "#7c3aed", "#0891b2", "#16a34a", "#ea580c", "#db2777", "#4f46e5", "#0d9488"];
const avatarColorFor = (name) => AVATAR_COLORS[(name || "?").charCodeAt(0) % AVATAR_COLORS.length];

export const UnassignConfirmationModal = ({
  assignees,
  groups: groupsProp,
  onClose,
  onConfirm,
  selectedAssignees,
  onToggleAssignee,
  onToggleAllAssignees,
  selectedGroupId,
  onSelectGroup,
  langMode,
  resetFilters
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [scope, setScope] = useState("users");
  const isGroupScope = scope === "groups";
  // Only show assignees who belong to a group assigned to this user
  // (blank group list => unrestricted => show all assignees).
  const allowedMemberIds = useMemo(() => {
    if (!getCurrentUserGroupIds().length) return null;
    return getGroupMemberIdSet(groupsProp);
  }, [groupsProp]);
  const list = useMemo(() => {
    const base = Array.isArray(assignees) ? assignees : [];
    if (!allowedMemberIds) return base;
    return base.filter((a) =>
      allowedMemberIds.has(String(a?.assigneeId ?? a?.userId ?? a?.id ?? ""))
    );
  }, [assignees, allowedMemberIds]);

  const filteredAssignees = useMemo(() => {
    const query = (searchQuery || "").trim().toLowerCase();
    if (!query) return list;
    return list.filter((assignee) => {
      const name = String(assignee?.assigneeName || assignee?.name || assignee?.userName || "").toLowerCase();
      return name.includes(query);
    });
  }, [list, searchQuery]);

  const getAssigneeId = (assignee) => assignee?.assigneeId || assignee?.userId || assignee?.id;

  const filteredIds = useMemo(
    () =>
      filteredAssignees
        .map((assignee) => getAssigneeId(assignee))
        .filter((id) => id !== undefined && id !== null && id !== ""),
    [filteredAssignees]
  );

  const groups = useMemo(() => normalizeGroups(groupsProp), [groupsProp]);
  // Only show groups that contain a currently-assigned user, mirroring the user list.
  const assignedGroups = useMemo(
    () =>
      filterGroupsByAssignedMembers(
        groups,
        list.map((assignee) => getAssigneeId(assignee))
      ),
    [groups, list]
  );
  const filteredGroups = useMemo(() => {
    if (!(searchQuery || "").trim()) return assignedGroups;
    return assignedGroups.filter((g) => groupMatchesQuery(g, searchQuery));
  }, [assignedGroups, searchQuery]);

  // Assignees and group are independent selections; either or both can be submitted.
  const canConfirm = selectedAssignees.length > 0 || selectedGroupId != null;

  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selectedAssignees.includes(id));

  const handleToggleAllFiltered = () => {
    if (!filteredIds.length) return;
    const shouldSelectAll = !allFilteredSelected;
    const selectedSet = new Set(selectedAssignees);

    filteredIds.forEach((id) => {
      const isSelected = selectedSet.has(id);
      if ((shouldSelectAll && !isSelected) || (!shouldSelectAll && isSelected)) {
        onToggleAssignee(id);
      }
    });
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-slate-900/40 backdrop-blur-[1px] z-[10000] p-4">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-2xl w-full max-w-3xl p-6 transform transition-all duration-300 scale-100 opacity-100">
        <div className="flex justify-between items-start mb-4 border-b border-slate-100 pb-4">
          <h3 className="text-lg font-semibold text-slate-800">
            {translations[langMode].unassigntask}
          </h3>
          <ModalCloseButton onClick={onClose} ariaLabel="Close unassign modal" />
        </div>

        <p className="text-sm text-slate-500 mb-4">
          {isGroupScope
            ? (translations[langMode]?.selectgrouptounassign ?? "Select group(s) to unassign.")
            : `${translations[langMode].selectassigntounassign}.`}
        </p>

        <AssignScopeTabs scope={scope} onScopeChange={setScope} langMode={langMode} />

        <div className="mb-3 flex items-center gap-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={
              isGroupScope
                ? (translations[langMode]?.searchGroup ?? "Search Groups")
                : (translations[langMode]?.searchfieldAgent ?? "Search Field Agents")
            }
            className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:border-[#236DB4] focus:outline-none focus:ring-2 focus:ring-[#236DB4]/20"
          />
          {!isGroupScope && (
            <CommonButton colorIntent="primary"
              type="button"
              variant="outline"
              onClick={handleToggleAllFiltered}
              disabled={filteredIds.length === 0}
              tooltipText={
                allFilteredSelected
                  ? (translations[langMode]?.deselectAllAgentsTooltip ?? "Deselect all visible assignees")
                  : (translations[langMode]?.selectAllAgentsTooltip ?? "Select all visible assignees")
              }
              tooltipPosition="top"
              className="!h-10 !min-h-[40px] !px-3 !text-xs !whitespace-nowrap"
            >
              {allFilteredSelected
                ? (translations[langMode]?.deselectAll ?? "Deselect All")
                : (translations[langMode]?.selectAll ?? "Select All")}
            </CommonButton>
          )}
        </div>

        <div className="bg-slate-50/60 rounded-lg p-2 mb-6 border border-slate-200">
          <div className="max-h-60 overflow-y-auto pr-1 custom-scrollbar">
            {isGroupScope ? (
              <GroupSelectList
                groups={filteredGroups}
                selectedGroupId={selectedGroupId}
                onSelectGroup={onSelectGroup}
                query={searchQuery}
                langMode={langMode}
              />
            ) : filteredAssignees.length > 0 ? (
              filteredAssignees.map((assignee) => (
                <label
                  key={getAssigneeId(assignee)}
                  className="flex items-center space-x-3 text-slate-700 cursor-pointer py-2 hover:bg-white rounded-lg transition px-2"
                >
                  <input
                    type="checkbox"
                    className="form-checkbox h-4 w-4 accent-[#236DB4] text-indigo-600 rounded"
                    checked={selectedAssignees.includes(getAssigneeId(assignee))}
                    onChange={() => onToggleAssignee(getAssigneeId(assignee))}
                  />
                  <div className="flex-1 flex items-center">
                    <span
                      className="h-8 w-8 rounded-full text-white flex items-center justify-center text-sm font-semibold mr-1 shrink-0"
                      style={{ background: avatarColorFor(assignee?.assigneeName || assignee?.name || assignee?.userName) }}
                    >
                      {(assignee?.assigneeName || assignee?.name || assignee?.userName || "?").charAt(0).toUpperCase()}
                    </span>
                    <span className="text-sm font-medium">{assignee?.assigneeName || assignee?.name || assignee?.userName || "Unknown"}</span>
                  </div>
                </label>
              ))
            ) : (
              <p className="text-sm text-slate-400 py-4 text-center">
                {translations[langMode]?.nofieldagent ?? "No assignees available"}
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-2">
          <CommonButton colorIntent="danger"
            onClick={onClose}
            variant="danger"
            tooltipText={translations[langMode].cancelandclose}
            tooltipPosition="top"
          >
            {translations[langMode].cancel}
          </CommonButton>
          <CommonButton colorIntent="warning"
            onClick={onConfirm}
            variant="primary"
            disabled={!canConfirm}
            tooltipText={translations[langMode].unassignselectedassingeesfromtask}
            tooltipPosition="top"
          >
            {translations[langMode].unassignn}
            {selectedAssignees.length > 0 ? ` (${selectedAssignees.length})` : ""}
          </CommonButton>
        </div>
      </div>
    </div>
  );
};
